import { useCallback, useEffect, useState } from "react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { sounds, setPlaceCustomUrl, setRemoveCustomUrl } from "@/lib/sounds";
import { logError } from "@/lib/logger";
import { compressImage } from "@/lib/imageResize";

const logErr = (msg) => (err) => logError(msg, err);

export function useBoard(user, setUser) {
  const [notes, setNotes] = useState([]);
  const [polaroids, setPolaroids] = useState([]);
  const [doneNotes, setDoneNotes] = useState([]);

  const loadAll = useCallback(async () => {
    try {
      const [n, p] = await Promise.all([api.get("/notes"), api.get("/polaroids")]);
      setNotes(n.data);
      setPolaroids(p.data);
    } catch (err) {
      logError("Failed to load board:", err);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // sync custom uploaded sounds from the user's saved profile
  const placeSound = user?.custom_place_sound;
  const removeSound = user?.custom_remove_sound;
  useEffect(() => {
    setPlaceCustomUrl(placeSound ? fileUrl(placeSound) : "");
    setRemoveCustomUrl(removeSound ? fileUrl(removeSound) : "");
  }, [placeSound, removeSound]);

  // real-time-ish polling for inbound notes
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const { data } = await api.get("/notes");
        setNotes((prev) => {
          const ids = new Set(prev.map((x) => x.id));
          const added = data.filter((x) => !ids.has(x.id));
          if (!added.length) return prev;
          sounds.arrival(added[0].channel);
          toast(`New note from ${added[0].sender_name || added[0].sender}`);
          return [...prev, ...added];
        });
      } catch (err) {
        logError("Note poll failed:", err);
      }
    }, 7000);
    return () => clearInterval(id);
  }, []);

  const nextZ = () => Math.max(0, ...notes.map((n) => n.z_index || 0), ...polaroids.map((p) => p.z_index || 0)) + 1;

  const bringFront = (id) => {
    const z = nextZ();
    setNotes((p) => p.map((n) => (n.id === id ? { ...n, z_index: z } : n)));
    setPolaroids((p) => p.map((n) => (n.id === id ? { ...n, z_index: z } : n)));
  };

  const noteMove = (id, x, y) => {
    const z = nextZ();
    setNotes((p) => p.map((n) => (n.id === id ? { ...n, x, y, z_index: z } : n)));
    api.patch(`/notes/${id}`, { x, y, z_index: z }).catch(logErr("Failed to move note:"));
  };

  const completeNote = async (id) => {
    setNotes((p) => p.filter((n) => n.id !== id));
    try {
      await api.post(`/notes/${id}/complete`);
      confetti({ particleCount: 40, spread: 55, origin: { y: 0.7 }, scalar: 0.7 });
    } catch (err) {
      logError("Failed to complete note:", err);
      toast.error("Couldn't complete that note — please try again.");
      loadAll();
    }
  };

  const deleteNote = async (id) => {
    sounds.peelOff();
    setNotes((p) => p.filter((n) => n.id !== id));
    try {
      await api.delete(`/notes/${id}`);
    } catch (err) {
      logError("Failed to delete note:", err);
      toast.error("Couldn't delete that note — please try again.");
      loadAll();
    }
  };

  const createNote = async (payload) => {
    try {
      const { data } = await api.post("/notes", payload);
      setNotes((p) => [...p, data]);
      sounds.place();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add note");
    }
  };

  const createBlankNote = async (color) => {
    try {
      const { data } = await api.post("/notes", { body: "", color, sender_name: "You", sender: "me@pinanote.app", channel: "manual" });
      setNotes((p) => [...p, data]);
      sounds.place();
      return data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add note");
      return null;
    }
  };

  const updateNoteText = async (id, fields) => {
    setNotes((p) => p.map((n) => (n.id === id ? { ...n, ...fields } : n)));
    try { await api.patch(`/notes/${id}`, fields); } catch (err) { logError("Failed to save note:", err); }
  };

  const polMove = (id, patch) => {
    const next = { ...patch };
    if (next.x !== undefined) next.z_index = nextZ();
    setPolaroids((p) => p.map((n) => (n.id === id ? { ...n, ...next } : n)));
    api.patch(`/polaroids/${id}`, next).catch(logErr("Failed to move polaroid:"));
  };

  const polCaption = (id, caption) => {
    setPolaroids((p) => p.map((n) => (n.id === id ? { ...n, caption } : n)));
    api.patch(`/polaroids/${id}`, { caption }).catch(logErr("Failed to caption polaroid:"));
  };

  const polDelete = (id) => {
    setPolaroids((p) => p.filter((n) => n.id !== id));
    api.delete(`/polaroids/${id}`).catch(logErr("Failed to delete polaroid:"));
  };

  const uploadPolaroid = async (file) => {
    const toUpload = await compressImage(file);
    const fd = new FormData();
    fd.append("file", toUpload);
    const tid = toast.loading("Pinning your photo…");
    try {
      const { data } = await api.post("/polaroids/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPolaroids((p) => [...p, data]);
      sounds.slap();
      toast.success("Polaroid pinned!", { id: tid });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed", { id: tid });
    }
  };

  const changeTheme = async (key) => {
    setUser({ ...user, theme: key });
    sounds.tack();
    try {
      await api.patch("/profile", { theme: key });
    } catch (err) {
      logError("Failed to save theme:", err);
    }
  };

  const openDonePile = async () => {
    try {
      const { data } = await api.get("/notes", { params: { status: "done" } });
      setDoneNotes(data);
    } catch (err) {
      logError("Failed to load done pile:", err);
    }
  };

  const restoreNote = async (id) => {
    try {
      await api.post(`/notes/${id}/restore`);
      setDoneNotes((p) => p.filter((n) => n.id !== id));
      loadAll();
    } catch (err) {
      logError("Failed to restore note:", err);
    }
  };

  const aiAction = async (noteId, action, model) => {
    const { data } = await api.post(`/notes/${noteId}/ai`, { action, model });
    if (action === "categorize") {
      setNotes((p) => p.map((n) => (n.id === noteId ? { ...n, color: data.color, tags: data.tags } : n)));
      const tagStr = data.tags?.length ? ` · ${data.tags.join(", ")}` : "";
      return `Recolored to ${data.color}${tagStr}`;
    }
    return data.result;
  };

  const aiDoodle = async (noteId) => {
    const { data } = await api.post(`/notes/${noteId}/doodle`);
    setPolaroids((p) => [...p, data]);
    sounds.slap();
    return data;
  };

  const transcribeAudio = async (blob) => {
    const extMap = { webm: "webm", mp4: "mp4", mpeg: "mp3", ogg: "ogg", wav: "wav", "x-m4a": "m4a", aac: "aac" };
    const sub = ((blob.type || "audio/webm").split("/")[1] || "webm").split(";")[0];
    const ext = extMap[sub] || "webm";
    const fd = new FormData();
    fd.append("file", blob, `voice.${ext}`);
    const { data } = await api.post("/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.text;
  };

  const transcribeVideo = async (polId) => {
    const { data } = await api.post(`/polaroids/${polId}/transcribe`);
    return data.text;
  };

  const blockSender = async (sender) => {
    const { data } = await api.post("/block", { sender });
    loadAll();
    return data.blocked;
  };

  const createEmoji = async (prompt) => {
    const { data } = await api.post("/emoji", { prompt });
    setPolaroids((p) => [...p, data]);
    sounds.slap();
    return data;
  };

  const tidyBoard = async () => {
    const tid = toast.loading("Tidying up your board…");
    try {
      await api.post("/board/tidy");
      await loadAll();
      sounds.slap();
      toast.success("All tidied up!", { id: tid });
    } catch {
      toast.error("Could not tidy the board", { id: tid });
    }
  };

  return {
    notes, polaroids, doneNotes,
    bringFront, noteMove, completeNote, deleteNote, createNote, createBlankNote, updateNoteText,
    polMove, polCaption, polDelete, uploadPolaroid,
    changeTheme, openDonePile, restoreNote,
    aiAction, aiDoodle,
    transcribeAudio, transcribeVideo, createEmoji, blockSender, tidyBoard,
  };
}
